import React, { useState } from "react";
import {
  Filter,
  Search,
  ChevronDown,
  X,
  ArrowUpDown,
  RefreshCw,
} from "lucide-react";

const AttendeesFilterSection = ({
  searchTerm,
  onSearchChange,
  ticketTypeFilter,
  onTicketTypeFilterChange,
  paymentStatusFilter,
  onPaymentStatusFilterChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
  onRefresh,
  ticketTypes = [],
}) => {
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const hasActiveFilters =
    searchTerm || ticketTypeFilter !== "all" || paymentStatusFilter !== "all";

  const clearAllFilters = () => {
    onSearchChange("");
    onTicketTypeFilterChange("all");
    onPaymentStatusFilterChange("all");
  };

  return (
    <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6">
      {/* Mobile Filter Toggle */}
      <div className="flex justify-between items-center mb-4 lg:hidden">
        <h3 className="flex items-center text-base font-semibold text-gray-900">
          <Filter className="mr-2 w-4 h-4 text-purple-600" />
          Filters
        </h3>
        <button
          onClick={() => setShowMobileFilters(!showMobileFilters)}
          className="flex items-center px-3 py-2 text-sm bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
        >
          <span>{showMobileFilters ? "Hide" : "Show"}</span>
          <ChevronDown
            className={`w-4 h-4 ml-2 transition-transform ${
              showMobileFilters ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {/* Desktop Filter Header */}
      <div className="hidden justify-between items-center mb-6 lg:flex">
        <h3 className="flex items-center text-lg font-semibold text-gray-900">
          <Filter className="mr-2 w-5 h-5 text-purple-600" />
          Filters & Search
        </h3>
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="flex items-center px-3 py-1.5 text-sm text-gray-600 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
            >
              <X className="mr-1 w-4 h-4" />
              Clear
            </button>
          )}
          <button
            onClick={onRefresh}
            className="flex items-center px-3 py-1.5 text-sm text-gray-600 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
            title="Refresh attendees"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filter Controls */}
      <div
        className={`space-y-4 ${
          showMobileFilters ? "block" : "hidden lg:block"
        }`}
      >
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by ID, name, email, or phone..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 text-sm sm:text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute right-4 top-1/2 text-gray-400 -translate-y-1/2 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter Grid */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
          {/* Ticket Type Filter */}
          <div className="relative">
            <select
              value={ticketTypeFilter}
              onChange={(e) => onTicketTypeFilterChange(e.target.value)}
              className="w-full pl-4 pr-10 py-3 text-sm border border-gray-200 rounded-xl appearance-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white cursor-pointer"
            >
              <option value="all">All Ticket Types</option>
              {ticketTypes.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 w-4 h-4 text-gray-400 pointer-events-none -translate-y-1/2" />
          </div>

          {/* Payment Status Filter */}
          <div className="relative">
            <select
              value={paymentStatusFilter}
              onChange={(e) => onPaymentStatusFilterChange(e.target.value)}
              className="w-full pl-4 pr-10 py-3 text-sm border border-gray-200 rounded-xl appearance-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white cursor-pointer"
            >
              <option value="all">All Payment Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 w-4 h-4 text-gray-400 pointer-events-none -translate-y-1/2" />
          </div>

          {/* Sort By */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => onSortByChange(e.target.value)}
              className="w-full pl-4 pr-10 py-3 text-sm border border-gray-200 rounded-xl appearance-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white cursor-pointer"
            >
              <option value="name">Sort by Name</option>
              <option value="ticket_type">Sort by Ticket Type</option>
              <option value="amount">Sort by Amount</option>
              <option value="date">Sort by Date</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 w-4 h-4 text-gray-400 pointer-events-none -translate-y-1/2" />
          </div>

          {/* Sort Order */}
          <div className="relative">
            <button
              onClick={() =>
                onSortOrderChange(sortOrder === "asc" ? "desc" : "asc")
              }
              className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-xl transition-colors hover:bg-gray-100"
            >
              <ArrowUpDown className="mr-2 w-4 h-4" />
              {sortOrder === "asc" ? "Ascending" : "Descending"}
            </button>
          </div>
        </div>

        {/* Mobile Clear Button */}
        {hasActiveFilters && (
          <div className="lg:hidden">
            <button
              onClick={clearAllFilters}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-xl transition-colors hover:bg-gray-100"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendeesFilterSection;
